// @flow
import * as React from 'react';
import { injectGlobal } from '../../emotion-import-helper.js';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type {
  CompilerInjectedProps,
  ValidationError,
} from '../../validation/runtime-checks/index.js';
import { productColor } from '../../designSystem.js';
import Figure from '../../Figure.js';
import { isAbortError } from '../../utilities.js';
import { useValidationApiOrigin } from '../../validation/ValidationApiOriginContext.js';

// Geogebra always internally produces a border which we don't want
injectGlobal`.GeoGebraFrame { border: none !important; }`;

// This is for telling Geogebra the class name of the DOM node that it
// should refer to when computing how to scale the material
const classNameForGeogebraScaling = '_151d0079-53e6-43f1-98b9-b57dcc1ac725';

export function INVGeogebra(props: {|
  materialId: string, // Eg. 'svqyuufx'
  width: number,
  height: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVGeogebra,
  );
  const mountNode = React.useRef(null);
  const [loadState, setLoadState] = React.useState<
    'loading' | 'success' | 'failure',
  >('loading');

  useInitializeApplet({
    materialId: props.materialId,
    mountNode,
    onLoadSuccess: React.useCallback(() => {
      // There is a bunch of layout jank internally in Geogebra for up
      // to 200ms after it thinks it has loaded. So we delay until that
      // has settled before we remove the cover in order to improve UX.
      setTimeout(() => {
        setLoadState('success');
      }, 200);
    }, []),
    onLoadFailure: React.useCallback(() => {
      setLoadState('failure');
    }, []),
  });

  useValidateGeogebraMaterial({
    _performValidation: props._performValidation,
    failedValidation,
    materialId: props.materialId,
    width: props.width,
    height: props.height,
    locations,
    addErrors,
  });

  if (failedValidation) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Figure>
        <div
          className={classNameForGeogebraScaling}
          style={{
            width: props.width,
            height: props.height,
            display: 'grid',
            gridTemplate: '1 / 1',
            borderRadius: 4,
            // Because so many of our Geogebra materials have a white background
            // we want a border to make the interactive area obvious.
            border: `1px solid ${productColor.iron}`,
            overflow: 'hidden',
          }}
        >
          <div
            // Ensure we get a new backing DOM node when we are going to perform
            // a .remove() and then .inject() in the geogebra initialization effect
            // as Geogebra can leave crap lying around in the DOM tree after .remove()
            key={props.materialId}
            style={{
              opacity: 1,
              gridArea: '1 / 1',
              // The applet will eventually mutate the width/height of this div
              // to match that of the node with the scaleContainerClass.
              // But they have a janky interstitial state before the scale
              // has been applied in which this div will be sized to the
              // "native" dimensions of the applet. To prevent this mis-sized
              // interstitial state we explicity set the dimensions.
              width: props.width,
              height: props.height,
            }}
            ref={mountNode}
          />
          <div
            style={{
              gridArea: '1 / 1',
              display: 'grid',
              placeItems: 'center',
              background: productColor.white,
              color:
                loadState === 'failure'
                  ? productColor.brickRed
                  : productColor.cloudburst,
              zIndex: 1000,
              transition: 'opacity 150ms ease',
              opacity: loadState === 'success' ? 0 : 1,
              // Ensure that this cover passes through the pointer events
              // only when its transparent (ie. in the loaded state)
              pointerEvents: loadState === 'success' ? 'none' : 'auto',
            }}
          >
            {loadState === 'loading' && 'Loading interactive...'}
            {loadState === 'failure' && 'Failed to load interactive'}
          </div>
        </div>
      </Figure>
    </div>
  );
}
INVGeogebra.spec = {
  materialId: t.string,
  width: t.number,
  height: t.number,
};

// Geogebra stupidly creates bindings in the global namespace for each appletObject
// so we use a unique per-component-instance id, combined with the material id,
// to avoid name collisions between applet instances in different components.
let componentInstanceId = 1;

function useInitializeApplet({
  materialId,
  mountNode,
  onLoadSuccess,
  // NOTE this does not handle *all* failure conditions as the Geogebra
  // bootstrapping script is poorly implemented and does not handle most
  // failure conditions. The two failure conditions that we handle here are
  //   a) Failure to load the the bootstrapping script
  //   b) Failure to pull the material from the API
  onLoadFailure,
}) {
  const [instanceId] = React.useState(() => componentInstanceId++);
  const appletId = `${materialId}-${instanceId}`;

  React.useEffect(() => {
    let staleEffect = false;

    // See: https://wiki.geogebra.org/en/Reference:GeoGebra_App_Parameters
    // NOTE these docs are shit and don't mention certain parts of the API
    const appletParameters = {
      // NOTE: this will eventually create a window[id] global
      // bound to the "appletObject" (not to be confused with the
      // GGBApplet instance)
      id: appletId,
      // 🚨 We consider scaling a Geogebra to fit a container to be an
      // anti-pattern as it produces unexpected and poor UX for students
      // when the Geogebra is scaled to different dimensions to those
      // used when creating the material (eg. only certain things in Geogebra
      // scale in an expected manner). Normally we want to specify explicit
      // width/height in the appletParameters, and enforce that the material
      // was made at those same dimensions (see GeogebraSubmission for the
      // ideal usage of this). We are only allowing this sub-standard behaviour
      // because INVGeogebra is for *ported legacy student lessons*. That is,
      // we are porting poorly made Geogebra's, so we can't tell people to remake
      // them at correct dimensions.
      scaleContainerClass: classNameForGeogebraScaling,
      allowUpscale: true, // anti-pattern for the same reasons as above
      material_id: materialId,
      // There is a horrendous bug that causes tapping into an input in the material
      // to put padding of 228px on the body of the document. It thinks it's trying
      // to show a keyboard (but no keyboard is shown). By forcing it to know that
      // a keyboard cannot be shown under any circumstances this idiotic behaviour
      // is avoided. Unclear if there is any way a material creator can actually
      // make one such that a keyboard is ever shown. We'll assume not for now.
      // Geogebra is designed like trash, and *everything* is loaded. Basically
      // there is no real difference between the calculator code and these little
      // widgets. All the same stuff is loaded, just a slightly different UI is shown.
      showKeyboardOnFocus: false,
      // NOTE: appletObject === window[appletId]
      appletOnLoad(_appletObject) {
        if (staleEffect) return;
        onLoadSuccess();
      },
      // This is invoked when the API request to fetch the material fails
      onError() {
        if (staleEffect) return;
        onLoadFailure();
      },
    };

    loadBootstrapScript()
      .then((GGBApplet) => {
        if (staleEffect) return;
        if (mountNode.current === null) return;
        const applet = new GGBApplet(appletParameters, '5.0');
        applet.inject(mountNode.current);
      })
      .catch(() => {
        if (staleEffect) return;
        onLoadFailure();
      });

    return () => {
      staleEffect = true;
      // There seems to be an undocumented .remove() method on the appletObject
      // which appears intended for resource cleanup. From profiling, however, Geogebra
      // still seems to leak some memory. Not going down that rabbit hole now.
      //
      // We could do something convoluted where we do "applet" pooling (akin
      // to the concept of object pooling). Not doing that at this stage as
      // we don't have the appetite for the time required for building robust
      // software.
      if (window[appletId]) {
        window[appletId].remove();
      }
    };
  }, [appletId, materialId, mountNode, onLoadSuccess, onLoadFailure]);
}

// Geogebra is really shit to load. It doesn't adhere to any modern
// standards of web development (it's written in Java, so this isn't
// too surprising I suppose)
//
// There is a small bootstrapping script `deployggb.js` which pretty much
// doesn't load anything meaningful at all (its only 40kb). It's main effect
// is to pollute the global namespace with some basic symbols; the most
// important of which is GGBApplet. Notably, this script isn't published as a
// module.
//
// When you actually instantiate a GGBApplet that's when most of the actual
// Geogebra code gets pulled down from their servers. This is also when the
// "ggb" for the specified materialId is pulled from their api server.
//
// loadBootstrapScript exists because 1) they don't publish this boostrapping
// script as a module; and 2) webpack doesn't appear to support ESM dynamic
// imports for remote resources; and 3) webpack only allows this syntax if
// you are using the unstable/experimental outputModule option (not interested
// in dealing with weird bugs in unstable bundler code as those are some of
// the worst kinds bugs to contend with).
//
// So this is effectively a poor man's equivalent of a dynamic ESM import:
//   return import('https://www.geogebra.org/apps/deployggb.js')
//
// The first time a INVGeogebra is mounted in a given JS runtime we will load,
// and cache this module. Any subsequent INVGeogebra component mounts will
// receive the cached outcome of the module loading.
//
// NOTE also that we don't do somethig silly like a <script> tag in the html file
// as that completely breaks React's component composition model. A component
// should declare all its dependencies.
let bootstrapPromise = null;
function loadBootstrapScript() {
  // TODO ideally this should be "retry-able". atm if the initial
  // promise is rejected, then every subsequent INVGeogebra will just
  // get that same rejected promise (and thus fail to load).
  if (bootstrapPromise !== null) return bootstrapPromise;
  bootstrapPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://www.geogebra.org/apps/deployggb.js';
    script.onload = () => resolve(window.GGBApplet);
    // NOTE onerror is never provided any information about why loading
    // a resource has failed; only that it failed to load.
    script.onerror = () => reject();
    const { body, head } = document;
    if (body && head) {
      body.appendChild(script);
    } else {
      reject();
    }
  });
  return bootstrapPromise;
}

function useValidateGeogebraMaterial({
  _performValidation,
  failedValidation,
  materialId,
  width,
  height,
  locations,
  addErrors,
}) {
  const validationApiOrigin = useValidationApiOrigin();

  React.useEffect(() => {
    if (_performValidation === false || failedValidation) return;

    const result = validateDimensions({
      width,
      height,
      locationData: locations.INVGeogebra,
    });
    if (result.type === 'invalid') {
      addErrors([result.error]);
    }

    const controller = new AbortController();
    validateGeogebraMaterial({
      validationApiOrigin,
      materialId,
      width,
      height,
      locationData: locations.materialId,
      signal: controller.signal,
    }).then((result) => {
      if (result.type === 'invalid') {
        addErrors([result.error]);
      }
    });

    return () => controller.abort();
  });
}

type ValidationResult =
  | {| type: 'valid' |}
  | {| type: 'invalid', error: ValidationError |};

async function validateGeogebraMaterial({
  validationApiOrigin,
  materialId,
  locationData,
  signal,
}): Promise<ValidationResult> {
  let json;
  try {
    const url = `${validationApiOrigin}/api/geogebra-material?materialId=${materialId}`;
    json = await (await fetch(url, { signal })).json();
  } catch (error) {
    if (isAbortError(error)) return { type: 'valid' };

    // Either the network request failed; or we got an unexpected non-json response.
    return {
      type: 'invalid',
      error: {
        message:
          'Could not determine validity of the material as network request failed. Erring on the side of caution and not letting this pass validation.',
        ...locationData,
      },
    };
  }
  // TODO handle non-compliant response shape
  const { item } = json.responses.response[1];

  if (item === undefined) {
    return {
      type: 'invalid',
      error: {
        message:
          'The Geogebra material could not be found. Make sure you are using a valid material id.',
        ...locationData,
      },
    };
  }

  // Leaving this here as we *want* this, but because this component needs to work for ported Geogebra
  // materials, which will be poorly made, we can't have this check running (yet... 🤞)
  // if (
  //   Number.parseFloat(item.width) !== width ||
  //   Number.parseFloat(item.height) !== height
  // ) {
  //   return {
  //     type: 'invalid',
  //     error: {
  //       message: `The Geogebra material must be made for the dimensions you are trying to use (${width}×${height}). Instead it is has been made for: ${item.width}×${item.height}.`,
  //       ...locationData,
  //     },
  //   };
  // }

  if (!['S', 'O'].includes(item.visibility)) {
    return {
      type: 'invalid',
      error: {
        message:
          'The Geogebra material must have the sharing setting set to Shared with Link or Public.',
        ...locationData,
      },
    };
  }

  // It is critical that we own the material, as otherwise the owner can just delete the material at any time
  // and break our published textbooks as a result.
  if (item.author !== 'mathspace') {
    return {
      type: 'invalid',
      error: {
        message: `The Geogebra material must be owned/authored by the mathspace user account. The author of the specified material is: "${item.author}"`,
        ...locationData,
      },
    };
  }

  return { type: 'valid' };
}

const validGeogebraDimensions = [
  { width: 596, height: 360 },
  { width: 596, height: 460 },
  { width: 596, height: 596 },
];

function validateDimensions({ width, height, locationData }): ValidationResult {
  const isValid = validGeogebraDimensions.some(
    (valid) => valid.width === width && valid.height === height,
  );

  if (isValid) return { type: 'valid' };

  return {
    type: 'invalid',
    error: {
      message: [
        `Invalid dimensions ${width}×${height}. Valid dimensions:`,
        validGeogebraDimensions
          .map(({ width, height }) => `  - ${width}×${height}`)
          .join('\n'),
      ].join('\n'),
      ...locationData,
    },
  };
}
